import { useTranslation } from 'react-i18next'

import { Badge, Card } from '@/design-system/primitives'
import { useAdminMutation, useAdminQuery } from '@/features/admin/api'
import type { CakeMoment } from '@/features/admin/types'
import { useApiErrorText } from '@/lib/staffGate'

import type { ConductorItem } from '../types'
import { text } from './shared'

/**
 * 🎂 Le Cake Moment, côté conducteur : est-il prévu, et quelle étape le
 * déclenche.
 *
 * Ses textes et son visuel se préparent dans l'administration ; le soir J, le
 * MC n'a besoin que de deux choses — l'activer (ou non) et le rattacher à la
 * bonne étape de son déroulé. Lancer cette étape l'affiche à l'écran.
 */
export function CakeCard({
  eventId,
  items,
  can,
}: {
  eventId: string | undefined
  items: ConductorItem[]
  can: (permission: string) => boolean
}) {
  const { t, i18n } = useTranslation()
  const errorText = useApiErrorText()
  const path = `/admin/events/${eventId}/cake-moment`
  const query = useAdminQuery<CakeMoment>(['admin-cake-moment', eventId], path, eventId, {
    retry: false,
  })
  const save = useAdminMutation<CakeMoment, Partial<CakeMoment>>('put', () => path, eventId, [
    ['admin-cake-moment', eventId],
    ['public-cake'],
    ['conductor-screen', eventId],
  ])
  const cake = query.data
  if (!cake) return null

  const canEdit = can('program.cake.update')
  const locale = i18n.language
  // Sans étape choisie, n'importe quelle étape « Cake Moment » le déclenche.
  const trigger = cake.program_item_id
    ? items.find((item) => item.id === cake.program_item_id)
    : items.find((item) => item.kind === 'cake_moment')
  const name = text(cake.name_i18n, locale)

  return (
    <Card className="space-y-3 p-4 sm:p-5">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="overline text-content-muted">{t('conductor.cockpit.cake.title')}</p>
          <p className="mt-1 break-words font-display text-heading-sm text-content-primary">
            {name === '—' ? t('conductor.cockpit.cake.unnamed') : name}
          </p>
        </div>
        <Badge tone={cake.is_active ? 'success' : 'neutral'}>
          {cake.is_active ? t('conductor.cockpit.cake.on') : t('conductor.cockpit.cake.off')}
        </Badge>
      </div>

      <p className="break-words text-caption text-content-secondary">
        {trigger
          ? t('conductor.cockpit.cake.trigger', { title: text(trigger.title_i18n, locale) })
          : t('conductor.cockpit.cake.noTrigger')}
      </p>

      {canEdit ? (
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="flex min-h-11 items-center gap-3 rounded-md border border-line px-3 text-sm text-content-primary">
            <input
              type="checkbox"
              className="h-5 w-5 shrink-0 accent-champagne-400"
              checked={cake.is_active}
              disabled={save.isPending}
              onChange={(change) => save.mutate({ is_active: change.target.checked })}
            />
            <span className="min-w-0">{t('conductor.cockpit.cake.active')}</span>
          </label>
          <label className="block text-caption text-content-secondary">
            {t('conductor.cockpit.cake.step')}
            <select
              className="mt-1 h-11 w-full min-w-0 rounded-md border border-line bg-bg-base px-3 text-sm text-content-primary"
              value={cake.program_item_id ?? ''}
              disabled={save.isPending}
              onChange={(change) => save.mutate({ program_item_id: change.target.value || null })}
            >
              <option value="">{t('conductor.cockpit.cake.anyStep')}</option>
              {items.map((item) => (
                <option key={item.id} value={item.id}>
                  {text(item.title_i18n, locale)}
                </option>
              ))}
            </select>
          </label>
        </div>
      ) : null}

      {save.isError ? <p className="text-sm text-state-danger">{errorText(save.error)}</p> : null}
    </Card>
  )
}
